import GreyLoadingOverlay from '@/components/loading/GreyLoadingOverlay';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Button,
  Dialog,
  Drawer,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { Typography } from 'design-system/components/Typography';
import Image from 'next/image';
import { PropsWithChildren, useContext } from 'react';
import { EPurchaseSectionStep, PurchaseSectionContext } from '.';
import { moneyFormat } from '../../../common/numberHelpers';
import { ZALO_OA_SUPPORT } from '../../../configs/externalLink';
import { ZaloIcon } from '../../../constant/imgImport';
import useCreateOrder from '../../../hooks/order/useCreateOrder';
import useToast from '../../../hooks/useToast';

const OrderInfo = ({ children }: PropsWithChildren<{}>) => {
  const { setOpenStep, openStep } = useContext(PurchaseSectionContext);

  const toggle = () => setOpenStep(undefined);

  const matches = useMediaQuery('(min-width:1000px)');

  return matches ? (
    <Dialog
      open={openStep === EPurchaseSectionStep.OrderInfo}
      onClose={toggle}
      maxWidth="md"
      fullWidth
      sx={{
        zIndex: 120,
      }}
    >
      <OrderInfo.Header toggle={toggle} />
      <div className="flex px-8">
        {children}
        <div className="flex w-[287px] flex-shrink-0 flex-col justify-end gap-2 border-l border-Other/Outlinedborder-stroke/30 py-4 pl-5">
          <OrderInfo.PriceCalculateReport />
        </div>
      </div>
      <div className="flex w-full items-center justify-between p-4">
        <OrderInfo.SupportButton />
        <OrderInfo.ConfirmButton />
      </div>
    </Dialog>
  ) : (
    <Drawer
      anchor={'bottom'}
      open={openStep === EPurchaseSectionStep.OrderInfo}
      onClose={toggle}
      PaperProps={{
        className: '!bg-[#FCFCFC] !rounded-[10px]',
      }}
      sx={{
        zIndex: 120,
      }}
    >
      <OrderInfo.Header toggle={toggle} />
      <div className="flex flex-col items-center px-4 pb-4">
        {children}
        <OrderInfo.PriceCalculateReport />
        <div className="mt-2 w-full">
          <OrderInfo.ConfirmButton />
        </div>
      </div>
      <div className="mx-4 flex justify-center border-t border-[#E0E0E0] py-4">
        <OrderInfo.SupportButton />
      </div>
    </Drawer>
  );
};

OrderInfo.Header = function Header({ toggle }: { toggle: () => void }) {
  return (
    <div className="flex items-center justify-between p-4 text-[#000000DE] md:p-6">
      <Typography variant="heading1">Thông tin thanh toán</Typography>
      <IconButton onClick={toggle}>
        <CloseRoundedIcon className="text-[#000000DE]" />
      </IconButton>
    </div>
  );
};

OrderInfo.PriceCalculateReport = function PriceCalculateReport() {
  const { price } = useContext(PurchaseSectionContext);

  return (
    <div className="flex w-full flex-col gap-2 text-sm font-medium leading-[120%] text-Text/Lightbackground/Primary">
      <p>Thông tin đơn hàng</p>
      <div className="flex items-center justify-between text-Text/Lightbackground/Secondary">
        <p className="flex-shrink">Tạm tính:</p>
        <p className="flex-grow text-right text-lg leading-[1em]">
          {moneyFormat(price || 0)} VNĐ
        </p>
      </div>
      <div className="flex items-center justify-between">
        <p className="w-auto break-words">Tổng thanh toán:</p>
        <p className="flex w-max flex-grow justify-end gap-1 text-2xl font-semibold leading-[140%] text-Success/Main">
          <span>{moneyFormat(price || 0)}</span>
          <span>VNĐ</span>
        </p>
      </div>
    </div>
  );
};

OrderInfo.SupportButton = function SupportButton() {
  return (
    <Button
      onClick={() => window.open(ZALO_OA_SUPPORT)}
      variant="low_emphasis_v1"
      className="!gap-2 !text-sm !font-medium !leading-4"
    >
      <Image src={ZaloIcon} alt="zalo icon" width={20} height={20} />
      Liên hệ hỗ trợ
    </Button>
  );
};

OrderInfo.ConfirmButton = function ConfirmButton() {
  const { setOpenStep, setOrder, productId, productType } = useContext(
    PurchaseSectionContext
  );
  const toast = useToast();

  const {
    mutationReturn: { mutate, isLoading },
  } = useCreateOrder({
    opt: {
      onSuccess(data) {
        setOpenStep(EPurchaseSectionStep.PaymentMethod);
        setOrder(data);
      },
      onError() {
        toast('Có lỗi xảy ra, vui lòng thử lại sau.', 'error');
      },
    },
  });

  return (
    <div className={`relative w-auto`}>
      <GreyLoadingOverlay show={isLoading} size={30} />
      <Button
        onClick={() =>
          mutate({
            orderDetails: [{ productId, productType }],
          })
        }
        fullWidth
        variant="high_emphasis_v1"
        className="!py-2"
      >
        Tiến hành thanh toán
      </Button>
    </div>
  );
};

export default OrderInfo;
